









































































import { Models } from '@mtap-smartcity/api';
import { Component, Vue, Prop } from 'vue-property-decorator';
import { BffSettings } from '@/api/bff';
import parametersMap from '@/constants/device_parameters_map';

interface DeviceProperty extends Models.Devices.DeviceDetailField {
  dataType?: string;
  value?: any;
  displayName?: string;
}

interface ParameterListElement {
  name: string;
  header: boolean;
  type?: 'global' | 'local';
  parameters: DeviceProperty[];
}

@Component
export default class DevicePropertiesList extends Vue {
  @Prop(Object) readonly deviceParameters!:
    Models.Devices.LampIotParameters |
    Models.Devices.LampIqrfParameters |
    Models.Devices.LampVeParameters |
    Models.Devices.LampControlCabinetParameters |
    Models.Devices.GatewayVeParameters |
    Models.Devices.GatewayControlCabinetParameters |
    Models.Devices.BridgeParameters |
    Record<string, string> |
    null;

  @Prop(Object) readonly deviceData!: Models.Devices.Device | null;

  @Prop(String) readonly tab!: 'device' | 'powerSupply' | 'controller' | 'deviceDetails';

  @Prop(String) readonly deviceType!: Models.Constants.DeviceType;

  @Prop(String) readonly controllerType!: Models.Constants.ControllerType | null;

  @Prop(Boolean) readonly disabled!: boolean;

  isFormValid = true;

  globalLampFields: DeviceProperty[] = [];

  globalGatewayFields: DeviceProperty[] = [];

  isAddingNewParam = false;

  newParamName = '';

  formRules = {
    longitude: [(v) => (v >= -180 && v <= 180) || !v || 'Podaj wartość między -180 a 180'],
    latitude: [(v) => (v >= -90 && v <= 90) || !v || 'Podaj wartość między -90 a 90'],
    subnetId: [(v) => (v >= 0 && v <= 255) || !v || 'Podaj wartość między 0 a 255'],
    netId: [(v) => (v >= 0 && v <= 255) || !v || 'Podaj wartość między 0 a 255'],
    daliShortAddress: [(v) => (v >= 0 && v <= 63) || !v || 'Podaj wartość między 0 a 63'],
    luminaires: [(v) => (v >= 1 && v <= 4) || !v || 'Podaj wartość między 1 a 4']
  };

  async fetchGlobalFields() {
    try {
      this.globalLampFields = await BffSettings.getDeviceDetailsSettings('lamp');
      this.globalGatewayFields = await BffSettings.getDeviceDetailsSettings('gateway');
    } catch (error) {
      console.error('Failed to fetch global fields:', error);
      this.globalLampFields = [];
      this.globalGatewayFields = [];
    }
  }

  get parameterList(): ParameterListElement[] {
    const params = this.deviceParameters ?? [];
    // if (!params) return [];

    const res: ParameterListElement[] = [];

    if (this.deviceData && this.tab === 'deviceDetails') {
      const objectDisplayNameField: DeviceProperty = {
        name: 'object_display_name',
        type: 'text',
        deprecated: false,
        renamed_to: null,
        value: this.deviceData.object_display_name
      };

      res.push({
        name: 'Device details',
        header: false,
        parameters: [objectDisplayNameField]
      });
    }

    // Handle deviceDetails tab which has dynamic Record<string, string> properties
    if (this.tab === 'deviceDetails' && params && typeof params === 'object') {
      const paramsObj = params as Record<string, string>;
      const global = this.globalDeviceParameters;
      const local: DeviceProperty[] = [];

      if (global.length > 0) {
        const globalWithValues = global.map((field) => ({
          ...field,
          value: paramsObj[field.name]
        }));

        res.push({
          name: this.deviceType === 'lamp' ? this.$t(`deviceDetails.forAllLamps`).toString() : this.$t(`deviceDetails.forAllGateways`).toString(),
          header: true,
          type: 'global',
          parameters: globalWithValues
        });
      }

      Object.keys(paramsObj).forEach((key) => {
        const names = global.map((f) => f.name);
        if (!names.includes(key)) {
          local.push({
            name: key,
            type: 'text',
            deprecated: false,
            renamed_to: null,
            value: paramsObj[key]
          });
        }
      });

      res.push({
        name: this.deviceType === 'lamp' ? this.$t(`deviceDetails.onlyForThisLamp`).toString() : this.$t(`deviceDetails.onlyForThisGateway`).toString(),
        header: true,
        type: 'local',
        parameters: local
      });
    } else if (params !== null) {
      const parametersToMap = this.deviceType === 'lamp' && this.tab === 'controller'
        ? parametersMap[this.deviceType][this.tab][this.controllerType!]
        : parametersMap[this.deviceType][this.tab];
      const result = (parametersToMap as any[]).map((p) => {
        if (p.dataType === 'date' && params[p.name]) {
          const value = (new Date(params[p.name])).toISOString()
            .substr(0, 10);
          const displayName = this.$t(`deviceParameters.${p.name}`);
          return {
            ...p,
            value,
            displayName
          };
        }
        const value = params[p.name] ?? null;
        const displayName = this.$t(`deviceParameters.${p.name}`);
        return {
          ...p,
          value,
          displayName
        };
      });

      if (!(result.length === 0)) {
        res.push({
          name: 'legacy',
          header: false,
          parameters: result
        });
      }
    }

    return res;
  }

  get globalDeviceParameters(): DeviceProperty[] {
    const global: DeviceProperty[] = [];

    if (this.deviceType === 'lamp') {
      this.globalLampFields.forEach((field) => {
        if (!field.deprecated) {
          global.push(field);
        }
      });
    } else if (this.deviceType === 'gateway') {
      this.globalGatewayFields.forEach((field) => {
        if (!field.deprecated) {
          global.push(field);
        }
      });
    }

    return global;
  }

  get rules() {
    return (paramName) => {
      const rule = this.formRules[paramName] ?? [];
      return rule;
    };
  }

  suffix(propName: string) {
    let suffix = '';
    switch (propName) {
      case 'serviceLife':
        suffix = this.$tc('main.year', 2);
        break;
      case 'luminousEfficiency':
        suffix = 'lm/W';
        break;
      case 'maxPower':
        suffix = 'W';
        break;
      default:
        break;
    }
    return suffix;
  }

  updateProperty(value, name) {
    if (name === 'object_display_name') {
      // Emit a different event for object_display_name since it's a top-level field
      this.$emit('update:objectDisplayName', { name, value });
      return;
    }
    const newVal = name === 'installationDate' ? new Date(value).toISOString() : value;
    this.$emit('update:deviceParameters', {
      name,
      value: newVal
    });
  }

  deleteLocalParameter(name: string) {
    this.$delete((this.deviceData as Models.Devices.Device).parameters['deviceDetails'] as Record<string, any>, name);
  }

  startAdding() {
    this.isAddingNewParam = true;
    this.newParamName = '';
    this.$nextTick(() => {
      const input = document.querySelector('.c-device-property__input input[autofocus]');
      if (input) (input as HTMLElement).focus();
    });
  }

  confirmAdd() {
    const name = this.newParamName.trim();
    if (name && this.deviceData) {
      const deviceDetails = this.deviceData.parameters['deviceDetails'] as Record<string, any>;
      if (!deviceDetails[name]) {
        this.$set(deviceDetails, name, '');
        this.isAddingNewParam = false;
        this.newParamName = '';
      } else {
        alert('Parametr o tej nazwie już istnieje');
      }
    }
  }

  cancelAdd() {
    this.isAddingNewParam = false;
    this.newParamName = '';
  }

  mounted() {
    this.fetchGlobalFields();
  }
}
